/**
 * Cable Schedule Generator
 * Creates consolidated cable schedules from calculation history
 */

import { CalculationHistoryEntry } from '../engine/types';
import { generatePDFDocument, formatCalculationTable, PDFSection } from './pdfExport';

export interface CableScheduleEntry {
  id: string;
  circuitName: string;
  source: string;
  destination: string;
  voltage: string;
  phase: string;
  loadAmps: number;
  wireSize: string;
  material: string;
  conductorCount: string;
  groundSize?: string;
  conduitSize?: string;
  conduitType?: string;
  lengthFeet?: number;
  voltageDrop?: number;
  notes?: string;
}

export interface CableScheduleOptions {
  projectName: string;
  preparedFor?: string;
  preparedBy?: string;
  entries: CableScheduleEntry[];
  includeVoltageDrop?: boolean;
  includeConduit?: boolean;
}

/**
 * Extract cable schedule data from calculation history
 */
export function extractCableScheduleEntry(
  entry: CalculationHistoryEntry,
  index: number
): CableScheduleEntry | null {
  const { calculatorType, inputs, outputs } = entry;

  // Only process relevant calculator types
  if (!['ampacity', 'voltage-drop', 'feeder', 'motor'].includes(calculatorType)) {
    return null;
  }

  const baseEntry: CableScheduleEntry = {
    id: entry.id,
    circuitName: entry.name || `Circuit ${index + 1}`,
    source: 'Panel',
    destination: 'Load',
    voltage: String(inputs.voltage || '480'),
    phase: String(inputs.phase || '3Φ'),
    loadAmps: Number(outputs.finalAmpacity || outputs.loadCurrent || 0),
    wireSize: String(inputs.wireSize || outputs.recommendedWireSize || '-'),
    material: String(inputs.material || 'copper'),
    conductorCount: String(inputs.conductorsPerPhase || 1),
    notes: '',
  };

  // Add calculator-specific data
  if (calculatorType === 'voltage-drop') {
    baseEntry.lengthFeet = Number(inputs.distance || 0);
    baseEntry.voltageDrop = Number(outputs.voltageDropPercent || 0);
  }

  if (calculatorType === 'feeder') {
    baseEntry.conduitSize = String(outputs.conduitSize || '-');
  }

  if (calculatorType === 'motor') {
    baseEntry.notes = `${inputs.hp || '-'} HP Motor`;
    baseEntry.loadAmps = Number(outputs.fla || 0);
  }

  return baseEntry;
}

/**
 * Generate cable schedule from history entries
 */
export function generateCableScheduleFromHistory(
  entries: CalculationHistoryEntry[]
): CableScheduleEntry[] {
  return entries
    .map((entry, index) => extractCableScheduleEntry(entry, index))
    .filter((entry): entry is CableScheduleEntry => entry !== null);
}

/**
 * Generate cable schedule HTML table
 */
export function generateCableScheduleTable(
  entries: CableScheduleEntry[],
  options: { includeVoltageDrop?: boolean; includeConduit?: boolean } = {}
): string {
  const { includeVoltageDrop = true, includeConduit = true } = options;

  const headers = [
    'Circuit',
    'Source',
    'Dest',
    'Voltage',
    'Phase',
    'Amps',
    'Wire',
    'Material',
    'Qty',
  ];

  if (includeConduit) {
    headers.push('Conduit');
  }

  if (includeVoltageDrop) {
    headers.push('Length', 'VD%');
  }

  headers.push('Notes');

  return `
    <table>
      <thead>
        <tr>
          ${headers.map(h => `<th>${h}</th>`).join('')}
        </tr>
      </thead>
      <tbody>
        ${entries.map(entry => `
        <tr>
          <td>${entry.circuitName}</td>
          <td>${entry.source}</td>
          <td>${entry.destination}</td>
          <td>${entry.voltage} V</td>
          <td>${entry.phase}</td>
          <td>${entry.loadAmps.toFixed(1)} A</td>
          <td>${entry.wireSize}</td>
          <td>${entry.material === 'copper' ? 'Cu' : 'Al'}</td>
          <td>${entry.conductorCount}</td>
          ${includeConduit ? `<td>${entry.conduitSize || '-'}</td>` : ''}
          ${includeVoltageDrop ? `
          <td>${entry.lengthFeet ? `${entry.lengthFeet}'` : '-'}</td>
          <td>${entry.voltageDrop ? `${entry.voltageDrop.toFixed(2)}%` : '-'}</td>
          ` : ''}
          <td>${entry.notes || '-'}</td>
        </tr>
        `).join('')}
      </tbody>
    </table>
  `;
}

/**
 * Export cable schedule as PDF
 */
export function exportCableSchedulePDF(options: CableScheduleOptions): void {
  const {
    projectName,
    preparedFor,
    preparedBy,
    entries,
    includeVoltageDrop = true,
    includeConduit = true,
  } = options;

  const tableHtml = generateCableScheduleTable(entries, {
    includeVoltageDrop,
    includeConduit,
  });

  // Calculate summary stats
  const totalCircuits = entries.length;
  const totalAmps = entries.reduce((sum, e) => sum + e.loadAmps, 0);
  const copperCount = entries.filter(e => e.material === 'copper').length;
  const aluminumCount = entries.filter(e => e.material === 'aluminum').length;

  const summaryData = [
    { label: 'Total Circuits', value: String(totalCircuits), unit: '' },
    { label: 'Total Connected Load', value: totalAmps.toFixed(1), unit: 'A' },
    { label: 'Copper Conductors', value: String(copperCount), unit: 'circuits' },
    { label: 'Aluminum Conductors', value: String(aluminumCount), unit: 'circuits' },
  ];

  const sections: PDFSection[] = [
    {
      title: 'Cable Schedule',
      content: tableHtml,
      type: 'table',
    },
    {
      title: 'Summary',
      content: formatCalculationTable(summaryData),
      type: 'table',
    },
  ];

  generatePDFDocument({
    title: 'Cable Schedule',
    subtitle: projectName,
    projectName,
    preparedFor,
    preparedBy,
    sections,
    includeNECReferences: false,
  });
}

/**
 * Export cable schedule as CSV
 */
export function exportCableScheduleCSV(entries: CableScheduleEntry[]): string {
  const headers = [
    'Circuit Name',
    'Source',
    'Destination',
    'Voltage',
    'Phase',
    'Load (A)',
    'Wire Size',
    'Material',
    'Conductor Count',
    'Ground Size',
    'Conduit Size',
    'Conduit Type',
    'Length (ft)',
    'Voltage Drop (%)',
    'Notes',
  ];

  const rows = entries.map(entry => [
    entry.circuitName,
    entry.source,
    entry.destination,
    entry.voltage,
    entry.phase,
    entry.loadAmps.toFixed(1),
    entry.wireSize,
    entry.material,
    entry.conductorCount,
    entry.groundSize || '',
    entry.conduitSize || '',
    entry.conduitType || '',
    entry.lengthFeet?.toString() || '',
    entry.voltageDrop?.toFixed(2) || '',
    entry.notes || '',
  ]);

  return [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
  ].join('\n');
}

export default {
  extractCableScheduleEntry,
  generateCableScheduleFromHistory,
  generateCableScheduleTable,
  exportCableSchedulePDF,
  exportCableScheduleCSV,
};
